import {
  captureLicenseScanResponseSchema,
  createLicenseScanTokenResponseSchema,
  listLicenseScansResponseSchema,
  resolveLicenseScanResponseSchema,
  type CaptureLicenseScanResponse,
  type CreateLicenseScanTokenResponse,
  type LicenseScanExtractedFields,
  type ListLicenseScansResponse,
  type ResolveLicenseScanRequest,
  type ResolveLicenseScanResponse,
} from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * The driver's-license scan client (`BACKLOG.md` C19) — three audiences, matching
 * `server/src/licenseScan/routes.ts`'s own split:
 *
 *   - `createLicenseScanToken`/`revokeLicenseScanToken` are **staff** calls, made from inside the
 *     ordinary authenticated app (`LicenseScanTokenPanel.tsx`), going through `apiRequest` exactly
 *     like every other staff API call — matching `api/kiosk.ts`'s identical split.
 *   - `captureLicenseScan` is the **kiosk-token-scoped** call, made from
 *     `routes/kiosk/LicenseScanCapturePage.tsx` with no staff session at all — carries the bearer
 *     token in an `Authorization` header, via `apiRequest`'s `headers` option.
 *   - `listLicenseScans`/`resolveLicenseScan` are **staff** calls again, made from
 *     `routes/patients/PatientLicenseScanReviewPage.tsx`.
 */

const revokeLicenseScanTokenResponseSchema = z.void();

export async function createLicenseScanToken(
  patientId: string,
): Promise<CreateLicenseScanTokenResponse> {
  return apiRequest('/api/license-scans/tokens', createLicenseScanTokenResponseSchema, {
    method: 'POST',
    body: { patientId },
  });
}

export async function revokeLicenseScanToken(tokenId: string): Promise<void> {
  await apiRequest(
    `/api/license-scans/tokens/${tokenId}/revoke`,
    revokeLicenseScanTokenResponseSchema,
    {
      method: 'POST',
      body: {},
    },
  );
}

function bearerHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export async function captureLicenseScan(
  token: string,
  extractedFields: LicenseScanExtractedFields,
): Promise<CaptureLicenseScanResponse> {
  return apiRequest('/api/license-scans/capture', captureLicenseScanResponseSchema, {
    method: 'POST',
    body: { extractedFields },
    headers: bearerHeaders(token),
  });
}

export async function listLicenseScans(
  patientId: string,
  signal?: AbortSignal,
): Promise<ListLicenseScansResponse> {
  return apiRequest(
    `/api/patients/${patientId}/license-scans`,
    listLicenseScansResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

export async function resolveLicenseScan(
  patientId: string,
  scanId: string,
  request: ResolveLicenseScanRequest,
): Promise<ResolveLicenseScanResponse> {
  return apiRequest(
    `/api/patients/${patientId}/license-scans/${scanId}/resolve`,
    resolveLicenseScanResponseSchema,
    { method: 'POST', body: request },
  );
}
